import isFinite from 'lodash-es/isFinite';
import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import type { FieldConfig } from '../../../../../components/field/FieldConfig';

export interface PriceRangeFormValues {
    minPrice: string;
    maxPrice: string;
}

const messages = defineMessages({
    minPrice: {
        id: 'search.filters.price.min',
        defaultMessage: 'Minimum',
    },
    maxPrice: {
        id: 'search.filters.price.max',
        defaultMessage: 'Maximum',
    },
    minGreaterThanMax: {
        id: 'search.filters.price.min.greaterThanMax',
        defaultMessage: 'Minimum price cannot be greater than maximum price',
    },
    maxLessThanMin: {
        id: 'search.filters.price.max.lessThanMin',
        defaultMessage: 'Maximum price cannot be less than minimum price',
    },
    minAtLeastZero: {
        id: 'search.filters.price.min.atLeastZero',
        defaultMessage: 'Minimum price must be at least 0',
    },
    maxAtLeastZero: {
        id: 'search.filters.price.max.atLeastZero',
        defaultMessage: 'Maximum price must be at least 0',
    },
    invalidNumber: {
        id: 'search.filters.price.invalidNumber',
        defaultMessage: 'Please enter a valid amount',
    },
});

export function usePriceRangeFields(
    form: UseFormReturn<PriceRangeFormValues>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
        getValues,
    } = form;
    const intl = useIntl();

    return {
        minPrice: {
            id: 'price-range-min-field',
            name: 'minPrice',
            label: intl.formatMessage(messages.minPrice),
            type: 'text',
            defaultValue: '',
            rules: {
                validate: (value: number | string): string | true => {
                    if (value === '') return true;

                    if (!isFinite(Number(value))) {
                        return intl.formatMessage(messages.invalidNumber);
                    }
                    if (
                        getValues('maxPrice') &&
                        value &&
                        Number(value) > Number(getValues('maxPrice'))
                    ) {
                        return intl.formatMessage(messages.minGreaterThanMax);
                    }
                    return true;
                },
                min: { value: 0, message: intl.formatMessage(messages.minAtLeastZero) },
            },
            error: errors.minPrice,
            control,
            variant: 'new-style',
        },
        maxPrice: {
            id: 'price-range-max-field',
            name: 'maxPrice',
            label: intl.formatMessage(messages.maxPrice),
            type: 'text',
            defaultValue: '',
            rules: {
                validate: (value: number | string): string | true => {
                    if (value === '') return true;

                    if (!isFinite(Number(value))) {
                        return intl.formatMessage(messages.invalidNumber);
                    }
                    if (
                        getValues('minPrice') &&
                        value &&
                        Number(value) < Number(getValues('minPrice'))
                    ) {
                        return intl.formatMessage(messages.maxLessThanMin);
                    }
                    return true;
                },
                min: { value: 0, message: intl.formatMessage(messages.maxAtLeastZero) },
            },
            error: errors.maxPrice,
            control,
            variant: 'new-style',
        },
    };
}
